import type { ReactNode } from "react";
import type { ComunicadoComRelacoes } from "@/modules/comunicados/services/comunicadosService";
import { rotuloDestino } from "@/modules/comunicados/utils/destino";
import { diasParaApagar, rotuloApagaEm, rotuloPublicado } from "@/modules/comunicados/utils/prazo";

interface CardComunicadoProps {
  comunicado: ComunicadoComRelacoes;
  onAbrir: (comunicado: ComunicadoComRelacoes) => void;
  /** Botões de ação (Editar, Arquivar, Apagar...) — só a gestão recebe. */
  acoes?: ReactNode;
}

function Etiqueta({ children, alerta = false }: { children: ReactNode; alerta?: boolean }) {
  return (
    <span
      className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ${
        alerta ? "bg-warning/20 text-text" : "bg-surface-2 text-muted"
      }`}
    >
      {children}
    </span>
  );
}

/**
 * Card de um comunicado na lista. Para o membro: título, trecho e data de
 * publicação — sem autor, destino nem prazo (o banco nem os entrega). Para
 * quem o banco libera os detalhes (gestão do elo/administrador): destino,
 * prazo e "Publicado por".
 */
export function CardComunicado({ comunicado: c, onAbrir, acoes }: CardComunicadoProps) {
  const agora = new Date();
  const apagaSozinho = c.situacao === "publicado" && c.apagar_automaticamente;

  return (
    <li className="rounded-2xl border border-border bg-surface p-4">
      <button type="button" onClick={() => onAbrir(c)} className="block w-full text-left">
        <span className="block text-[15px] font-bold leading-snug text-text">{c.titulo}</span>
        <span className="mt-1 line-clamp-3 block whitespace-pre-line text-[13px] text-muted">{c.corpo}</span>
      </button>

      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1.5 text-xs text-muted">
        <span>{rotuloPublicado(c.publicado_em, agora)}</span>
        {c.situacao === "arquivado" && <Etiqueta>Arquivado</Etiqueta>}
        {c.editado_em && <Etiqueta>Editado</Etiqueta>}
        {c.detalhesVisiveis && (
          <>
            <Etiqueta>{rotuloDestino(c)}</Etiqueta>
            {c.situacao === "arquivado" ? (
              <Etiqueta>Só apaga se alguém apagar</Etiqueta>
            ) : apagaSozinho ? (
              <Etiqueta alerta={diasParaApagar(c.contagem_inicio_em, agora) <= 3}>
                {rotuloApagaEm(c.contagem_inicio_em, agora)}
              </Etiqueta>
            ) : (
              <Etiqueta>Fica até alguém apagar</Etiqueta>
            )}
          </>
        )}
      </div>

      {c.detalhesVisiveis && c.autorNome && (
        <p className="mt-1.5 text-xs text-muted">Publicado por {c.autorNome}</p>
      )}

      {acoes && <div className="mt-3 flex flex-wrap gap-2">{acoes}</div>}
    </li>
  );
}
